import React from 'react';

import { Checkbox, DatePicker, Select, type TypeSelectOption } from '@thefarmersfront/kitchen';

import type { CustomCellRendererProps } from 'ag-grid-react';
import { useShallow } from 'zustand/react/shallow';

import { useReceiveStore } from '~/store';
import { formatDate, getDateByOffset } from '~/utils';

import type { TypeInputStatus } from '~/types/common.types';
import type { TypeReceiveRequestRegisterModalGoods } from '~/types/receive.types';

import styles from './ReceiveRequestDateCell.module.scss';

const DATE_TYPE_OPTIONS = [
  { value: 'expirationDate', text: '유통/소비기한' },
  { value: 'manufactureDate', text: '제조일자' },
];

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > '기한/일자 사용여부' 셀 렌더러
 */
const ReceiveRequestDateCell = ({
  node: { rowIndex },
}: CustomCellRendererProps<TypeReceiveRequestRegisterModalGoods>) => {
  const [requestModalGoodsList, setRequestModalGoodsList, requestModalGoodsListStatus, setRequestModalGoodsListStatus] =
    useReceiveStore(
      useShallow((state) => [
        state.requestModalGoodsList,
        state.setRequestModalGoodsList,
        state.requestModalGoodsListStatus,
        state.setRequestModalGoodsListStatus,
      ])
    );

  if (rowIndex === null) {
    return <div>-</div>;
  }
  const rowData = requestModalGoodsList[rowIndex];

  if (!rowData) {
    return <div>-</div>;
  }

  const handleChangeIsDateUsed = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    setRequestModalGoodsList([
      ...requestModalGoodsList.slice(0, rowIndex),
      {
        ...requestModalGoodsList[rowIndex],
        dateUseYn: checked,
        dateType: checked ? DATE_TYPE_OPTIONS[0].value : null,
        date: null,
      },
      ...requestModalGoodsList.slice(rowIndex + 1),
    ]);

    if (!checked) {
      resetRowError();
    }
  };

  const handleChangeDateType = (_: unknown, option: TypeSelectOption<string>) => {
    setRequestModalGoodsList([
      ...requestModalGoodsList.slice(0, rowIndex),
      {
        ...requestModalGoodsList[rowIndex],
        dateType: option.value,
        date: null,
      },
      ...requestModalGoodsList.slice(rowIndex + 1),
    ]);

    resetRowError();
  };

  const handleChangeDate = (date: Date | null) => {
    const formattedDate = formatDate(date, 'YYYY-MM-DD');

    if (formattedDate === rowData['date']) {
      return;
    }

    setRequestModalGoodsList([
      ...requestModalGoodsList.slice(0, rowIndex),
      {
        ...requestModalGoodsList[rowIndex],
        date: formattedDate,
      },
      ...requestModalGoodsList.slice(rowIndex + 1),
    ]);

    resetRowError();
  };

  const resetRowError = () => {
    setRequestModalGoodsListStatus([
      ...requestModalGoodsListStatus.slice(0, rowIndex),
      {
        ...requestModalGoodsListStatus[rowIndex],
        date: 'default' as TypeInputStatus,
      },
      ...requestModalGoodsListStatus.slice(rowIndex + 1),
    ]);
  };

  return (
    <div className={styles['date-cell-container']}>
      <Checkbox checked={rowData['dateUseYn']} size="medium" label="사용" onChange={handleChangeIsDateUsed} />
      <div className={styles['date-cell-inputs']}>
        <Select
          className={styles['date-cell-input']}
          size="tiny"
          maxWidth="112px"
          placeholder="종류 선택"
          dropdownPortalTarget={document.body}
          shouldCloseOnNotInView
          disabled={!rowData['dateUseYn']}
          value={rowData['dateType'] ? DATE_TYPE_OPTIONS.find((option) => option.value === rowData['dateType']) : null}
          options={DATE_TYPE_OPTIONS}
          onChange={handleChangeDateType}
        />
        <DatePicker
          className={styles['date-cell-input']}
          size="tiny"
          placeholder="날짜 선택"
          portalTarget={document.body}
          shouldCloseOnNotInView
          disabled={!rowData['dateUseYn']}
          minDate={rowData['dateType'] === 'expirationDate' ? getDateByOffset(0, 'day') : undefined}
          status={requestModalGoodsListStatus[rowIndex]?.['date'] ?? 'default'}
          value={rowData['date'] ? new Date(rowData['date']) : null}
          onChange={handleChangeDate}
        />
      </div>
    </div>
  );
};

export default ReceiveRequestDateCell;
